// Client for the issue-report API (/api/report-issue) — the "Report issue"
// toolbar button. One POST carries the note the user typed plus a base64
// screenshot of the page; the lambda writes both to S3 and emails them to the
// people who fix things (terraform: modules/lambda/report-issue.tf).
//
// Throwing style, like gmail.js: this is a deliberate
// submit, and a submit that quietly does nothing is worse than one that says
// why it failed. The modal keeps the typed text on failure, so nothing is lost.
import { getIdToken } from './cognito.js'
import { recentClientErrors } from './clientErrors.js'

// Same escape hatch every other module here has: `npm run start` without
// API_PROXY answers /api/* with index.html and a 200, so a non-JSON response
// means "no API on this origin" — retry the deployed stage directly.
const ISSUE_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

async function authHeaders(extra = {}) {
  const token = await getIdToken()
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function apiJson(res) {
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    const err = new Error(`Issue reporting is unavailable (${res.status})`)
    err.status = res.status
    throw err
  }
  const data = await res.json()
  if (!res.ok) {
    // Keep the server's own words AND the status — 401 (expired session) and
    // 403 (not a member of this workspace) need different advice.
    const err = new Error(data.error || `Issue reporting failed (${res.status})`)
    err.status = res.status
    throw err
  }
  return data
}

// Everything about the page that a person debugging this would otherwise have
// to ask for. All of it is already visible to the signed-in user.
function pageContext() {
  const nav = typeof navigator === 'undefined' ? {} : navigator
  return {
    page: {
      url: window.location.href,
      path: window.location.pathname + window.location.search,
      title: document.title,
    },
    context: {
      // Injected by webpack's DefinePlugin (webpack.config.js). typeof-guarded
      // because a build that does not define it must not throw here.
      app_version: typeof __APP_BUNDLE__ === 'string' ? __APP_BUNDLE__ : '',
      user_agent: nav.userAgent || '',
      local_time: new Date().toString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        dpr: window.devicePixelRatio || 1,
      },
      screen: {
        width: window.screen?.width || 0,
        height: window.screen?.height || 0,
      },
      theme: document.documentElement.getAttribute('data-theme') || 'light',
      errors: recentClientErrors(),
    },
  }
}

/**
 * File one issue report.
 *
 * `screenshot` is a data URL (or '' when the capture failed — the note still
 * goes). Resolves to { ok, id, note_key, screenshot_key, emailed, email_error }:
 * `emailed: false` means the report IS stored, the notification just did not
 * send, and the caller should say so rather than claim success.
 */
export async function reportIssue({ message, screenshot = '' }) {
  const body = JSON.stringify({
    message,
    screenshot: screenshot || '',
    ...pageContext(),
  })
  const init = {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body,
  }

  // A rejected fetch (offline, CORS, DNS, the route not deployed yet) must not
  // surface as "Failed to fetch". That is the browser's words, not an answer,
  // and the person reading it is already having a bad enough day to be filing
  // a bug report.
  const post = async (url) => {
    try {
      return await fetch(url, init)
    } catch {
      return null
    }
  }

  let res = await post('/api/report-issue')
  const ct = res ? res.headers.get('content-type') || '' : ''
  if ((!res || !ct.includes('application/json')) && ISSUE_FALLBACK_BASE) {
    res = await post(`${ISSUE_FALLBACK_BASE}/report-issue`)
  }
  if (!res) {
    const err = new Error(
      'Issue reporting could not be reached from this browser. Your note has ' +
        'not been sent — check your connection, or send it to Jackson directly.',
    )
    err.status = 0
    throw err
  }
  return apiJson(res)
}
